import { postApacheUserToken } from "@/services/Login";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import Spinner from "@/components/atoms/Spinner/Spinner";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import { useTranslation } from "react-i18next";
import CTA from "@/components/atoms/CTA";
import { useSearchParams } from "react-router-dom";
import { SidebarLayoutContainer, SidebarLayoutContent, SidebarLayoutMenu } from "@/components/atoms/SidebarLayout";
import ReportsNavigation from "@/components/organisms/ReportsNavigation";
import { Stack } from "@mantine/core";
import { getRuntimeConfig } from "@/utils/runtimeConfig";

const DASHBOARD_MAP: Record<string, string | undefined> = {
  sales: getRuntimeConfig("APACHE_SALES_UUID"),
  buy: getRuntimeConfig("APACHE_BUY_UUID"),
  esg: getRuntimeConfig("APACHE_ESG_UUID"),
};

const SUPERSET_URL = getRuntimeConfig("APACHE_URL");

export const Reports = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tokenRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentCompany } = useAuth();
  const [searchParams] = useSearchParams();
  const queryDashboardKey = searchParams.get("dashboardKey") ?? undefined;
  const { t } = useTranslation();

  const dashboardId = queryDashboardKey ? DASHBOARD_MAP[queryDashboardKey] : DASHBOARD_MAP["sales"];

  const { mutateAsync: getApacheUserToken } = useMutation<any, unknown, string | undefined>({
    mutationFn: async (id?: string) => {
      const idToUse = id ?? dashboardId;
      if (!idToUse) {
        throw new Error("Missing DASHBOARD_ID");
      }
      return await postApacheUserToken(idToUse, "no");
    },
    onError: () => {
      setError("Kunne ikke laste inn rapportene");
      setLoading(false);
    },
    onSuccess: () => {
      setError(null);
      setLoading(false);
    },
  });

  const loadDashboard = useCallback(async () => {
    if (!dashboardId || !SUPERSET_URL) {
      setError("Missing configuration for dashboard.");
      return;
    }

    if (!containerRef.current) {
      setError("Mount element not available.");
      return;
    }

    if (!currentCompany) {
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const res = await getApacheUserToken(dashboardId);
      const token = (res as any)?.data?.token;
      if (!token) {
        setError("No token returned from server.");
        return;
      }
      tokenRef.current = token;

      const urlParams = { companyId: currentCompany?.companyId, lang: "no" };

      await embedDashboard({
        id: dashboardId,
        supersetDomain: SUPERSET_URL,
        mountPoint: containerRef.current,
        fetchGuestToken: async () => (await getApacheUserToken(dashboardId)).data.token,
        dashboardUiConfig: {
          filters: { visible: true },
          hideTitle: true,
          hideChartControls: true,
          urlParams: urlParams,
        },
      });

      const iframe = containerRef.current.querySelector("iframe");
      if (iframe) {
        (iframe as HTMLIFrameElement).style.width = "100%";
        (iframe as HTMLIFrameElement).style.height = "100%";
        (iframe as HTMLIFrameElement).style.border = "none";
        (iframe as HTMLIFrameElement).style.display = "block";
      }
    } catch {
      setError(t("notifications:generic.loadError"));
    } finally {
      setLoading(false);
    }
  }, [getApacheUserToken, dashboardId, queryDashboardKey, currentCompany]);

  useEffect(() => {
    loadDashboard().then(() => {});
  }, [loadDashboard]);

  return (
    <>
      <title>{`Reviver - ${t("navigation:reports")}`}</title>
      <meta name="description" content={`Reviver - ${t("navigation:reports")}`} />

      <SidebarLayoutContainer>
        <SidebarLayoutMenu>
          <ReportsNavigation />
        </SidebarLayoutMenu>
        <SidebarLayoutContent stretch={true}>
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "85vh",
              overflow: "hidden",
            }}
          >
            {loading && <Spinner loading={loading} />}
            {/* Show error UI when an error exists */}
            {error && (
              <Stack justify={"center"} align={"center"} w={"100%"} h={"100%"} bg={"white"}>
                <Text>{error}</Text>
                <CTA
                  variant={"primary"}
                  onClick={async () => {
                    setError(null);
                    await loadDashboard();
                  }}
                >
                  {t("actions:retry")}
                </CTA>
              </Stack>
            )}
            <div
              ref={containerRef}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
            {!loading && !error && !dashboardId && (
              <Group justify={"center"} align={"center"} w={"100%"} h={"100%"}>
                <Text>{t("reports.noDashboardConfigured", "No dashboard configured for this route.")}</Text>
              </Group>
            )}
          </div>
        </SidebarLayoutContent>
      </SidebarLayoutContainer>
    </>
  );
};

export default Reports;
